import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { SuccessFeeTranche } from '../../api/types'
import { InfoHint } from '../../components/InfoHint'
import { formatCzk, formatRate, formatUnits, formatUnits4 } from './format'

interface RedemptionTranchesTableProps {
  tranches: SuccessFeeTranche[]
  /** Celková odměna odkupu (Σ fee tranší) — kontrolní součet pod tabulkou. */
  totalFee: number
  /** Benchmark p.a. (gate), default 7 %. */
  benchmarkPa?: number
}

/**
 * Rozpad výkonnostní odměny odkupu per tranše (= per vklad). Stejné sloupce i
 * stejná mechanika jako roční běh success fee — odkup jen počítá nad menším
 * rozsahem jednotek (odkupovaná část držby).
 */
export function RedemptionTranchesTable({
  tranches,
  totalFee,
  benchmarkPa,
}: RedemptionTranchesTableProps) {
  const benchmarkText = formatRate(benchmarkPa ?? 0.07)

  if (tranches.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Rozpad odměny není k dispozici (odkup nemá tranše).
      </Typography>
    )
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Datum vkladu</TableCell>
            <TableCell align="right">Jednotky</TableCell>
            <TableCell align="right">Investováno</TableCell>
            <TableCell align="right">Nákupní cena</TableCell>
            <TableCell align="right">Hodnota</TableCell>
            <TableCell align="right">
              Výchozí hodnota{' '}
              <InfoHint
                lines={[
                  'Od čeho se počítá zisk: investovaná částka, případně jednotky × nejvyšší cena z minulých zúčtování (high-water mark).',
                  'Už jednou zpoplatněný zisk se tak neplatí podruhé.',
                ]}
              />
            </TableCell>
            <TableCell align="right">Zisk</TableCell>
            <TableCell align="right">
              Zhodnocení p.a.{' '}
              <InfoHint
                lines={[
                  'Zhodnocení tranše nad výchozí hodnotou, přepočtené na p.a. konvencí Actual/360 dle smlouvy.',
                  'Období běží od posledního zúčtování odměny, u prvního od data vkladu.',
                ]}
              />
            </TableCell>
            <TableCell align="center">
              Benchmark{' '}
              <InfoHint
                lines={[
                  `ANO = zhodnocení PŘEVÝŠILO ${benchmarkText} p.a. → odměna se platí z celého zisku tranše.`,
                  'NE = odměna za tuto tranši je 0, i když je v zisku.',
                ]}
              />
            </TableCell>
            <TableCell align="right">Odměna</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tranches.map((t, index) => (
            <TableRow key={(t.depositId ?? 'x') + '-' + index}>
              <TableCell>
                {t.date ? new Date(t.date).toLocaleDateString('cs-CZ') : 'bez data'}
              </TableCell>
              <TableCell align="right">{formatUnits4(t.units)}</TableCell>
              <TableCell align="right">{formatCzk(t.invested)}</TableCell>
              <TableCell align="right">
                {t.buyPrice !== null && t.buyPrice !== undefined ? formatUnits(t.buyPrice) : '—'}
              </TableCell>
              <TableCell align="right">{formatCzk(t.value)}</TableCell>
              <TableCell align="right">{formatCzk(t.baselineValue)}</TableCell>
              <TableCell align="right">{formatCzk(t.profit)}</TableCell>
              <TableCell align="right">{(t.returnPa * 100).toFixed(2)} %</TableCell>
              <TableCell align="center">{t.benchmarkMet ? 'ANO' : 'NE'}</TableCell>
              <TableCell align="right">{formatCzk(t.fee)}</TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ '& td': { fontWeight: 600 } }}>
            <TableCell colSpan={9}>Výkonnostní odměna celkem</TableCell>
            <TableCell align="right">{formatCzk(totalFee)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  )
}
